import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  useTheme,
  alpha,
  LinearProgress
} from '@mui/material';
import {
  Factory,
  Login,
  AdminPanelSettings
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import usePageTitle from '../hooks/usePageTitle';

const Dashboard = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const theme = useTheme();

  const isAdmin = user?.is_staff || user?.role === 'admin';

  usePageTitle('Manufacturing Dashboard');

  useEffect(() => {
    if (isAuthenticated && user) {
      if (isAdmin) {
        navigate('/manufacturing');
      } else {
        navigate('/shop-floor/all');
      }
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, user, isAdmin, navigate]);

  if (isAuthenticated && loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
          <LinearProgress sx={{ width: '100%', maxWidth: 400, mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Redirecting...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper 
        elevation={3}
        sx={{ 
          p: { xs: 2, md: 6 }, 
          textAlign: 'center',
          borderRadius: 3,
          background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.03)} 0%, ${alpha(theme.palette.background.paper, 1)} 100%)`
        }}
      >
        <Factory sx={{ fontSize: 80, color: 'primary.main', mb: 3 }} />
        
        <Typography variant="h3" gutterBottom fontWeight={700} color="primary.main">
          Gopak Manufacturing
        </Typography>
        
        <Typography variant="h6" color="text.secondary" sx={{ mb: 5 }}>
          Manufacturing Control System
        </Typography>

        <Box 
          sx={{ 
            p: 3, 
            borderRadius: 2, 
            bgcolor: alpha(theme.palette.info.main, 0.08),
            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
            mb: 3
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
            <Login color="info" />
            <Typography variant="subtitle1" fontWeight={600} color="info.main">
              Please log in to access services
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Use the login button in the top right corner
          </Typography>
        </Box>

        <Box 
          sx={{ 
            p: 2, 
            borderRadius: 2, 
            bgcolor: alpha(theme.palette.grey[500], 0.08),
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <AdminPanelSettings fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              Need an account? Contact your system administrator
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default Dashboard;